/**
 * playbookEligibility (DEV-1282) — which organizations a user may RUN a
 * given playbook in. Shared by InstantiatePlaybookModal (the "Organization
 * running this project" picker) and CommunityPlaybooksTab (which hides a
 * playbook entirely when the viewer can't run it anywhere — Skip,
 * 2026-06-09: "don't even show it if they can't create from it").
 *
 * Eligible org = an org-family community where the user is owner / admin /
 * content_creator, OR the user's own workshop. A PRIVATE playbook is
 * restricted to its creating org.
 */

import { normalizeGuid } from "../../utils/guid";
import { pathFromLeafGuid, pathDisplay } from "../../utils/entityPath";

export interface RunOrgOption {
  guid: string;
  label: string;
}

/** Community roles that let a user run a project on behalf of an org. */
const RUN_ROLES = new Set(["owner", "admin", "content_creator"]);

export function eligibleRunOrgs(
  playbook: { scope?: string | null; ownerCommunityGuid: string },
  currentUserGuid: string,
  communities: Array<Record<string, any>>,
  relationships: Array<Record<string, any>>,
  communityRoles: Array<Record<string, any>>,
  /** System admins are authorized in every org (matches isElevatedViewer +
   *  the backend create_projects sysadmin fast-path). */
  isSysadmin = false,
): RunOrgOption[] {
  const upperUser = (currentUserGuid || "").toUpperCase();
  if (!upperUser) return [];

  // Communities where the user DIRECTLY holds a run-role.
  const roleGuids = new Set<string>();
  for (const r of communityRoles || []) {
    if (
      String(r.user_guid || "").toUpperCase() === upperUser &&
      (r.is_active ?? true) !== false &&
      RUN_ROLES.has(String(r.role || "").toLowerCase())
    ) {
      roleGuids.add(String(r.community_entity_guid || "").toUpperCase());
    }
  }

  // Parent map (child → parent) so authority inherits DOWN the org tree:
  // an admin/content_creator at a parent (e.g. a Rotary district) can run a
  // playbook in any subcommunity beneath it. Mirrors the backend
  // HasCommunityCreateProjects ancestor walk.
  const parentByChild = new Map<string, string>();
  for (const rel of relationships || []) {
    const child = String(rel?.child_entity_guid ?? rel?.childEntityGuid ?? "").toUpperCase();
    const parent = String(rel?.parent_entity_guid ?? rel?.parentEntityGuid ?? "").toUpperCase();
    const rt = String(rel?.relationship_type ?? rel?.relationshipType ?? "").toLowerCase();
    if (child && parent && (rt === "parent_of" || rt === "contains")) {
      parentByChild.set(child, parent);
    }
  }

  // True when the user holds a run-role on this community OR any ancestor.
  const hasAuthority = (up: string): boolean => {
    let cur = up;
    const seen = new Set<string>();
    for (let hop = 0; hop < 10; hop++) {
      if (roleGuids.has(cur)) return true;
      if (seen.has(cur)) break;
      seen.add(cur);
      const parent = parentByChild.get(cur);
      if (!parent) break;
      cur = parent;
    }
    return false;
  };

  const scopePrivate = playbook.scope === "private";
  const ownerUp = String(playbook.ownerCommunityGuid || "").toUpperCase();

  const out: RunOrgOption[] = [];
  for (const e of communities || []) {
    const guid = normalizeGuid(e?.entity_guid || e?.entityGuid);
    if (!guid) continue;
    const up = guid.toUpperCase();
    const isActive = e?.is_active ?? e?.isActive ?? true;
    if (!isActive) continue;

    const et = String(e?.entity_type ?? e?.entityType ?? "").toLowerCase();
    // A personal workshop is identified by its personal owner back-link
    // (DEV-896), regardless of entity_type. Such an entity is ONLY eligible
    // for its own owner — never for other users, and never via the sysadmin
    // bypass (you don't run a playbook inside someone else's personal space).
    const personalOwner = String(
      e?.personal_owner_user_guid ?? e?.personalOwnerUserGuid ?? "",
    ).toUpperCase();
    const isWorkshop = personalOwner.length > 0 || et === "solo_creator_workshop";
    const isOrg = !isWorkshop && (et === "organization" || et.startsWith("org_"));

    const eligible = isWorkshop
      ? personalOwner === upperUser
      : isOrg && (isSysadmin || hasAuthority(up));
    if (!eligible) continue;

    // Private playbook → only its creating org may run it (sysadmin bypasses).
    if (scopePrivate && !isSysadmin && up !== ownerUp) continue;

    const leaf = String(e?.name ?? "").trim();
    if (!leaf) continue;
    const rootToLeaf = pathFromLeafGuid(guid, communities as any, relationships as any);
    const parts = (rootToLeaf ?? []).map((p: any) => String(p?.name ?? "").trim()).filter(Boolean);
    const ancestors = parts.slice(0, -1).reverse();
    out.push({ guid, label: ancestors.length ? `${leaf} — ${pathDisplay(ancestors)}` : leaf });
  }
  out.sort((a, b) => a.label.localeCompare(b.label));
  return out;
}
